import { Anotacoes, Marca, Ponto, Texto, Traco } from './tipos';

const PREFIXO = '@anotacoes';
const CASAS_DECIMAIS = 4;

/**
 * As anotacoes ficam presas ao usuario logado (`@fId`), senao os rabiscos de um professor
 * reapareceriam para o proximo aluno que abrisse a mesma lesson no computador da escola.
 * Sem sessao (material aberto pela rota publica) fica so o prefixo.
 */
function chaveArmazenamento() {
	const functionId = localStorage.getItem('@fId');

	return functionId ? `${PREFIXO}:${functionId}` : PREFIXO;
}

function ehPonto(valor: any): valor is Ponto {
	return !!valor && typeof valor.x === 'number' && typeof valor.y === 'number';
}

function ehTraco(valor: any): valor is Traco {
	return (
		!!valor &&
		(valor.ferramenta === 'caneta' || valor.ferramenta === 'marcador') &&
		typeof valor.cor === 'string' &&
		typeof valor.espessura === 'number' &&
		Array.isArray(valor.pontos) &&
		valor.pontos.every(ehPonto)
	);
}

function ehTexto(valor: any): valor is Texto {
	return (
		!!valor &&
		valor.ferramenta === 'texto' &&
		typeof valor.cor === 'string' &&
		typeof valor.tamanho === 'number' &&
		(valor.fundo === undefined || typeof valor.fundo === 'string') &&
		typeof valor.conteudo === 'string' &&
		valor.conteudo.length > 0 &&
		ehPonto(valor.posicao)
	);
}

function ehMarca(valor: any): valor is Marca {
	return ehTraco(valor) || ehTexto(valor);
}

/** Le o que estiver salvo, descartando qualquer entrada malformada: o storage e editavel pelo usuario. */
export function carregarAnotacoes(): Anotacoes {
	try {
		const bruto = localStorage.getItem(chaveArmazenamento());
		if (!bruto) return {};

		const dados = JSON.parse(bruto);
		if (!dados || typeof dados !== 'object') return {};

		const anotacoes: Anotacoes = {};
		Object.keys(dados).forEach((chave) => {
			if (!Array.isArray(dados[chave])) return;

			const marcas = dados[chave].filter(ehMarca);
			if (marcas.length > 0) anotacoes[chave] = marcas;
		});

		return anotacoes;
	} catch (erro) {
		console.warn('Anotacoes salvas ilegiveis, comecando do zero.', erro);

		return {};
	}
}

export function salvarAnotacoes(anotacoes: Anotacoes) {
	const paginas = Object.keys(anotacoes).filter((chave) => anotacoes[chave].length > 0);

	try {
		if (paginas.length === 0) {
			localStorage.removeItem(chaveArmazenamento());

			return;
		}

		// Coordenadas com 4 casas dao precisao de fracao de pixel e cortam bastante do JSON.
		const arredondar = (ponto: Ponto) => ({
			x: Number(ponto.x.toFixed(CASAS_DECIMAIS)),
			y: Number(ponto.y.toFixed(CASAS_DECIMAIS))
		});

		const compacto: Anotacoes = {};
		paginas.forEach((chave) => {
			compacto[chave] = anotacoes[chave].map(
				(marca) =>
					marca.ferramenta === 'texto'
						? { ...marca, posicao: arredondar(marca.posicao) }
						: { ...marca, pontos: marca.pontos.map(arredondar) }
			);
		});

		localStorage.setItem(chaveArmazenamento(), JSON.stringify(compacto));
	} catch (erro) {
		// Storage cheio ou bloqueado (aba anonima): o quadro segue funcionando so em memoria.
		console.warn('Nao foi possivel salvar as anotacoes do material.', erro);
	}
}
